import { Link } from 'react-router-dom';
import type { SprayStatus, SprayTargetLot } from '@/types/spray';

interface Props {
  lots: SprayTargetLot[];
  status: SprayStatus;
}

/**
 * The lots a nursery spray landed on. Until the spray is completed this is
 * the plan; at completion it is written again with what stood there, because
 * stock moves while a spray waits.
 */
function SprayTargetLotsCard({ lots, status }: Props) {
  const plants = lots.reduce((n, l) => n + (Number(l.plant_count) || 0), 0);
  const sprayed = lots.length > 0 && lots.every(l => l.basis === 'sprayed');

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">Lots sprayed</h3>
        <span className="text-sm text-neutral-600">
          {plants.toLocaleString()} plant{plants === 1 ? '' : 's'} in {lots.length} lot{lots.length === 1 ? '' : 's'}
        </span>
      </div>
      <p className="text-xs text-neutral-500">
        {sprayed
          ? 'What stood in these rows and places when the spray was completed.'
          : status === 'cancelled'
            ? 'What was planned. The spray was cancelled, so nothing went on.'
            : 'As planned. Written again when the spray is completed, with what is standing there then.'}
      </p>
      {lots.length === 0 ? (
        <p className="text-sm text-neutral-500">Nothing was standing in these rows and places.</p>
      ) : (
        <ul className="divide-y divide-neutral-100 border border-neutral-100 rounded-lg">
          {lots.map(l => (
            <li key={l.id} className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 px-3 py-2 text-sm">
              <span className="min-w-0 break-words">
                {l.lot_id ? (
                  <Link to={`/nursery/lots/${l.lot_id}`} className="font-medium text-primary-700 hover:underline">
                    {l.cultivar_name ?? 'Lot'}{l.rootstock_name ? ` / ${l.rootstock_name}` : ''}
                  </Link>
                ) : (
                  <span className="font-medium text-neutral-800">
                    {l.cultivar_name ?? 'Lot'}{l.rootstock_name ? ` / ${l.rootstock_name}` : ''}
                  </span>
                )}
                <span className="text-xs text-neutral-500">
                  {' '}· {[l.tree_type_name, l.lot_code, l.location_name].filter(Boolean).join(' · ')}
                </span>
              </span>
              <span className="text-neutral-700">{Number(l.plant_count).toLocaleString()}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default SprayTargetLotsCard;
